/* eslint-disable lingui/no-unlocalized-strings -- intentionally not translated */
import { Link, useLocation } from '@tanstack/react-router';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import clsx from 'clsx';
import DocPageIcon from '../Docs/DocPageIcon';
import {
  activeGroupFromPath,
  groupIndexRoute,
  type DocGroupKey,
} from '../Docs/docGroupRoutes';
import { useMobileNavSidebar } from '../../hooks/useUserPreferences';

// Pre-filled support mail. Contact Support is an action, not a content group,
// so it is a mailto link rather than an in-app route.
const SUPPORT_HREF = 'mailto:support@instapaper.com?subject=Instapaper%20Docs%20Help';

interface GroupNavItem {
  key: DocGroupKey;
  label: string;
  /** Phosphor icon name resolved by DocPageIcon. */
  icon: string;
}

// Built inside the component (as a function) so `t` runs at render time and the
// labels re-translate on locale change. Order matches the product spec.
function getGroupItems(): GroupNavItem[] {
  return [
    { key: 'documentation', label: t`Documentation`, icon: 'Notebook' },
    { key: 'developers', label: t`Developers`, icon: 'Code' },
    { key: 'about-legal', label: 'About & Legal', icon: 'Info' },
  ];
}

/**
 * Persistent footer navigation pinned to the bottom of the docs-center sidebar.
 * Lets the reader jump between the top-level groups (and Contact Support) from
 * anywhere in the docs; the group matching the current route is highlighted.
 */
function DocsGroupFooterNav() {
  useLingui();
  const pathname = useLocation({ select: (l) => l.pathname });
  const { closeMobileNavSidebar } = useMobileNavSidebar();
  const activeGroup = activeGroupFromPath(pathname);

  return (
    <nav className="docs-group-footer" aria-label={t`Documentation sections`}>
      <ul className="list-reset">
        {getGroupItems().map((item) => {
          const isActive = item.key === activeGroup;
          return (
            <li key={item.key}>
              <Link
                to={groupIndexRoute[item.key]}
                onClick={closeMobileNavSidebar}
                data-active={isActive ? 'true' : undefined}
                aria-current={isActive ? 'page' : undefined}
                className={clsx('side-item docs-group-footer-item gap-2')}
              >
                <DocPageIcon
                  name={item.icon}
                  weight={isActive ? 'regular' : 'light'}
                  className="flex-shrink-0"
                />
                <span>{item.label}</span>
              </Link>
            </li>
          );
        })}
        <li>
          <a
            href={SUPPORT_HREF}
            onClick={closeMobileNavSidebar}
            className="side-item docs-group-footer-item gap-2"
          >
            <DocPageIcon name="Lifebuoy" className="flex-shrink-0" />
            <span>{t`Contact Support`}</span>
          </a>
        </li>
      </ul>
    </nav>
  );
}

export default DocsGroupFooterNav;
